import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import IconButton from "./Button";
import { UploadImageIcon, CloseIcon } from "./Icons";
import TextField from "./TextField";

const ImageUploader = ({
  onDataUpdate,
  imageFromParent = null,
  altTextFromParent,
}) => {
  const [imagePreview, setImagePreview] = useState(imageFromParent);
  const [editVisible, setEditVisible] = useState(!!imageFromParent); // Show edit button if there's an initial image
  const [inputKey, setInputKey] = useState(Date.now());
  const [altText, setAltText] = useState(altTextFromParent || "");

  // Track if we've selected/removed an image
  const isImageChanged = useRef(false);
  const fileRef = useRef(null); // We store the actual File object here

  useEffect(() => {
    setImagePreview(imageFromParent); // Update image if `imageFromParent` prop changes
    setEditVisible(!!imageFromParent); // Show edit button if image exists
  }, [imageFromParent]);

  useEffect(() => {
    setAltText(altTextFromParent || "");
  }, [altTextFromParent]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result); // Show preview
      fileRef.current = file;
      setEditVisible(true);

      isImageChanged.current = true; // Mark image as changed
      onDataUpdate({
        file,
        preview: reader.result,
        altText,
        imageChanged: true,
      });
    };

    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setEditVisible(false);
    fileRef.current = null;
    isImageChanged.current = true;

    onDataUpdate({
      file: null,
      preview: null,
      altText: "",
      imageChanged: true,
    });

    // Reset the input by setting the inputKey to a new value
    setInputKey(Date.now()); // This will effectively recreate the input element

    // Clear the file input
    const fileInput = document.getElementById(`upload-button-${inputKey}`);
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleAltTextChange = (value) => {
    setAltText(value);
    onDataUpdate({
      file: fileRef.current || null,
      preview: imagePreview,
      altText: value,
      imageChanged: isImageChanged.current, // Send the current state of image changed
    });
  };

  return (
    <Container>
      <label htmlFor={`upload-button-${inputKey}`}></label>
      <ImageUploadContainer image={imagePreview}>
        {editVisible && (
          <EditButton onClick={handleRemoveImage}>
            <CloseIcon color="#FFF" />
          </EditButton>
        )}
        {!imagePreview && (
          <ButtonWrapper>
            <IconButton
              variant="ghost"
              state="idle"
              icon={<UploadImageIcon />}
              onClick={() =>
                document.getElementById(`upload-button-${inputKey}`).click()
              }
            >
              Upload image
            </IconButton>
          </ButtonWrapper>
        )}
      </ImageUploadContainer>

      <InputWrapper>
        <TextField
          placeholder="Enter alt text"
          value={altText}
          onChange={(e) => handleAltTextChange(e.target.value)}
          name="altText"
          showCharacterLimit={true}
          maxLength={200}
        />
      </InputWrapper>

      <Input
        id={`upload-button-${inputKey}`}
        type="file"
        accept="image/*"
        onChange={handleImageChange}
        style={{ display: "none" }}
      />
    </Container>
  );
};

export default ImageUploader;

const Container = styled.div`
  display: flex;
  align-items: center; // Align vertically
`;

const ImageUploadContainer = styled.div`
  position: relative;
  width: 200px;
  height: 200px;
  border-radius: 12px;
  border: 1px solid #d9d9d9;
  background: #d9d9d9;
  align-items: center;
  justify-content: center;
  background-image: url(${(props) => props.image});
  background-size: cover;
  background-position: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ButtonWrapper = styled.div`
  max-width: 200px;
  align-items: center;
  display: flex;
  height: 100%;
`;

const InputWrapper = styled.div`
  margin-left: 16px;
  flex-grow: 1;
`;

const EditButton = styled.div`
  position: absolute;
  top: -10px;
  right: -10px;
  display: inline-flex;
  padding: 8px;
  align-items: center;
  gap: 10px;
  border-radius: 100px;
  background: var(--Colors-Primary-500, #0065c0);
  color: white;
  cursor: pointer;
`;

const Input = styled.input`
  display: none;
`;
